import React from "react";
import { Info } from "lucide-react";
import { Button } from "./ui/button.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover.tsx";
import { cn } from "../lib/utils.ts";

export type ServiceStatusItem = {
  key: string;
  label: string;
  colorBg: string;
  textColor?: string;
  description: string;
};

export const SERVICE_STATUS_ITEMS: ServiceStatusItem[] = [
  {
    key: "scheduled",
    label: "Scheduled",
    colorBg: "bg-[#facc15]",
    textColor: "text-[#1f2937]",
    description: "Booked and assigned to technician; pending confirmation",
  },
  {
    key: "confirmed",
    label: "Confirmed",
    colorBg: "bg-[#dc2626]",
    textColor: "text-white",
    description: "Confirmed by customer via link in SMS or email",
  },
  {
    key: "paid",
    label: "Paid",
    colorBg: "bg-[#65a30d]",
    textColor: "text-white",
    description: "Deposit or full service fee received from customer",
  },
  {
    key: "done",
    label: "Done",
    colorBg: "bg-[#0f766e]",
    textColor: "text-white",
    description: "Service completed by technician and ready for pickup",
  },
  {
    key: "client_not_show_up",
    label: "No show",
    colorBg: "bg-[#111827]",
    textColor: "text-white",
    description: "Customer did not arrive for scheduled service appointment",
  },
  {
    key: "canceled",
    label: "Canceled",
    colorBg: "bg-[#64748b]",
    textColor: "text-white",
    description: "Canceled by shop, customer, or due to timeout (24h)",
  },
  {
    key: "leave",
    label: "Technician leave",
    colorBg: "bg-[#7e22ce]",
    textColor: "text-white",
    description: "Planned vacation or absence of technician",
  },
];

export const DYNO_STATUS_ITEMS: ServiceStatusItem[] = [
  {
    key: "draft",
    label: "Draft",
    colorBg: "bg-[#2d9bf0]",
    textColor: "text-white",
    description: "Available dyno slot open for customer booking",
  },
  {
    key: "added",
    label: "Reserved",
    colorBg: "bg-[#facc15]",
    textColor: "text-[#1f2937]",
    description: "Booked by customer; awaiting confirmation (within 24h)",
  },
  {
    key: "confirmed",
    label: "Confirmed",
    colorBg: "bg-[#dc2626]",
    textColor: "text-white",
    description: "Confirmed by customer via link in SMS or email",
  },
  {
    key: "paid",
    label: "Paid",
    colorBg: "bg-[#65a30d]",
    textColor: "text-white",
    description: "Dyno appointment deposit or full fee paid",
  },
  {
    key: "done",
    label: "Done",
    colorBg: "bg-[#0f766e]",
    textColor: "text-white",
    description: "Dyno session completed",
  },
  {
    key: "client_not_show_up",
    label: "No show",
    colorBg: "bg-[#111827]",
    textColor: "text-white",
    description: "Customer did not arrive for scheduled dyno appointment",
  },
  {
    key: "canceled",
    label: "Canceled",
    colorBg: "bg-[#64748b]",
    textColor: "text-white",
    description: "Canceled by shop, customer, or due to timeout (24h)",
  },
  {
    key: "day_off",
    label: "Day off",
    colorBg: "bg-[#ffc107]",
    textColor: "text-[#111827]",
    description: "Day marked as non-working / closed",
  },
  {
    key: "leave",
    label: "Technician leave",
    colorBg: "bg-[#7e22ce]",
    textColor: "text-white",
    description: "Planned vacation or absence of technician",
  },
];

export type StatusLegendType = "service" | "dyno";

const getItemsForType = (type: StatusLegendType) =>
  type === "dyno" ? DYNO_STATUS_ITEMS : SERVICE_STATUS_ITEMS;

export const ServiceStatusInlineLegend = ({
  type = "service",
  className,
}: {
  type?: StatusLegendType;
  className?: string;
}) => {
  const items = getItemsForType(type);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-xs text-slate-600",
        className
      )}
      aria-label={`${type === "dyno" ? "Dyno" : "Service"} status legend`}
    >
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        Status:
      </span>
      {items.map((item) => (
        <span
          key={item.key}
          className="inline-flex items-center gap-1.5"
          title={item.description}
        >
          <span
            className={cn(
              "h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-slate-900/10",
              item.colorBg
            )}
            aria-hidden="true"
          />
          <span className="text-slate-700">{item.label}</span>
        </span>
      ))}
    </div>
  );
};

export const ServiceStatusLegend = ({
  type = "service",
  className,
}: {
  type?: StatusLegendType;
  className?: string;
}) => {
  const items = getItemsForType(type);
  const isDyno = type === "dyno";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-9 gap-1.5 px-2.5 text-xs font-medium text-slate-700",
            className
          )}
          aria-label={`${isDyno ? "Dyno" : "Service"} status legend`}
          title={`${isDyno ? "Dyno" : "Service"} status legend`}
        >
          <Info className="h-4 w-4 text-sky-600" />
          <span>Legend</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3 shadow-lg">
        <div className="mb-2 border-b border-slate-100 pb-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-800">
            {isDyno ? "Dyno Status Legend" : "Service Status Legend"}
          </h4>
          <p className="text-[11px] text-slate-500">
            {isDyno
              ? "Color indicators for dyno appointment slots and technician leaves"
              : "Color indicators for calendar bookings and technician leaves"}
          </p>
        </div>
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.key} className="flex items-start gap-2.5 text-xs">
              <span
                className={cn(
                  "mt-0.5 inline-block h-3 w-3 shrink-0 rounded-full shadow-sm ring-1 ring-slate-900/10",
                  item.colorBg
                )}
                aria-hidden="true"
              />
              <div className="leading-tight">
                <span className="font-semibold text-slate-900">
                  {item.label}
                </span>
                <p className="text-[11px] text-slate-500 leading-normal">
                  {item.description}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default ServiceStatusLegend;
